import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './core/login/login.component';
import { ToastrModule } from 'ngx-toastr';
import {
  NgxUiLoaderModule,
  NgxUiLoaderConfig,
  NgxUiLoaderHttpModule,
} from 'ngx-ui-loader';
import { APP_BASE_HREF } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { UnhandledExceptionsHandler } from './interceptor/error.handler';
import { HttpErrorInterceptor } from './interceptor/error.interceptor';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AuthGuard } from './auth/auth.guard';
import { LoginApiServiceService } from './services/login-api-service.service';
import { AuthService } from './auth/auth.service';
import { TokenInterceptor } from './auth/token.interceptor';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MaterialModule } from './material.module';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';

import { HomeComponent } from './modules/exam-ui/home/home.component';
import { AboutComponent } from './modules/exam-ui/about/about.component';
import { ContactComponent } from './modules/exam-ui/contact/contact.component';
import { UserDetailsComponent } from './modules/exam-ui/user-details/user-details.component';
import { ExamComponent } from './modules/exam-ui/exam/exam.component';
import { ResultComponent } from './modules/exam-ui/result/result.component';
import { ExamHeaderComponent } from './modules/exam-ui/exam-header/exam-header.component';
import { ExamFooterComponent } from './modules/exam-ui/exam-footer/exam-footer.component';
import { ExamNavigationComponent } from './modules/exam-ui/exam-navigation/exam-navigation.component';
import { ExamQrComponent } from './modules/exam-ui/exam-qr/exam-qr.component';
import { UpdateUserStatusComponent } from './modules/exam-ui/update-user-status/update-user-status.component';
import { BlogDetailsComponent } from './modules/exam-ui/blog-details/blog-details.component';
import { BlogsComponent } from './modules/exam-ui/blogs/blogs.component';
import { ForumsComponent } from './modules/exam-ui/forums/forums.component';
import { UserDashboardComponent } from './modules/exam-ui/user-dashboard/user-dashboard.component';
import { FaqComponent } from './modules/exam-ui/faq/faq.component';
import { UserLoginComponent } from './modules/exam-ui/user-login/user-login.component';
import { ChapterQuestionsComponent } from './modules/exam-ui/chapter-questions/chapter-questions.component';
import { ClientLoginComponent } from './modules/exam-ui/login/login.component';

import { QRCodeModule } from 'angularx-qrcode';
import { ChatBotComponent } from './shared/chat-bot/chat-bot.component';
import { MarkdownModule, MarkdownService } from 'ngx-markdown';
import {} from 'ngx-markdown';
import { environment } from 'src/environments/environment';

import { SlickCarouselModule } from 'ngx-slick-carousel';
import { EventDetailsComponent } from './modules/exam-ui/event-details/event-details.component';
import { EventsComponent } from './modules/exam-ui/events/events.component';
import { SignUpComponent } from './modules/exam-ui/sign-up/sign-up.component';
import { ForumDetailsComponent } from './modules/exam-ui/forum-details/forum-details.component';
import { ForumQuestionComponent } from './modules/exam-ui/forum-question/forum-question.component';

import { PdfViewerModule } from 'ng2-pdf-viewer';
import { BookViewerComponent } from './modules/exam-ui/book-viewer/book-viewer.component';
import { AddInfoComponent } from './modules/exam-ui/add-info/add-info.component';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { AuthGuardAdmin } from './auth/auth.guardAdmin';
import { SignUpSimpleComponent } from './modules/exam-ui/sign-up-simple/sign-up-simple.component';
import { MoreInfoComponent } from './modules/exam-ui/more-info/more-info.component';
import { MindsetshiftComponent } from './modules/exam-ui/mindsetshift/mindsetshift.component';
import { UserInfoComponent } from './modules/exam-ui/user-info/user-info.component';
import { ForgotPasswordComponent } from './modules/exam-ui/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './modules/exam-ui/reset-password/reset-password.component';
import { SummaryImageComponent } from './modules/exam-ui/summary-image/summary-image.component';

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: 'rgb(15,15,177)',
  bgsOpacity: 0.5,
  bgsPosition: 'bottom-right',
  bgsSize: 60,
  bgsType: 'rectangle-bounce-pulse-out-rapid',
  blur: 5,
  delay: 0,
  fastFadeOut: true,
  fgsColor: 'rgb(245,158,11)',
  fgsPosition: 'center-center',
  fgsSize: 60,
  fgsType: 'rectangle-bounce-pulse-out',
  gap: 24,
  logoPosition: 'center-center',
  logoSize: 120,
  logoUrl: '',
  masterLoaderId: 'master',
  overlayBorderRadius: '0',
  overlayColor: 'rgba(40, 40, 40, 0.8)',
  pbColor: 'rgb(245,158,11)',
  pbDirection: 'ltr',
  pbThickness: 3,
  hasProgressBar: true,
  text: 'Loading..',
  textColor: '#FFFFFF',
  textPosition: 'center-center',
  maxTime: -1,
  minTime: 300,
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    UserDetailsComponent,
    ExamComponent,
    ResultComponent,
    ExamHeaderComponent,
    ExamFooterComponent,
    ExamNavigationComponent,
    ExamQrComponent,
    UpdateUserStatusComponent,
    BlogDetailsComponent,
    BlogsComponent,
    UserDetailsComponent,
    ForumsComponent,
    UserDashboardComponent,
    FaqComponent,
    UserLoginComponent,
    ChapterQuestionsComponent,
    ClientLoginComponent,
    ExamQrComponent,
    ChatBotComponent,
    EventDetailsComponent,
    EventsComponent,
    SignUpComponent,
    ForumDetailsComponent,
    ForumQuestionComponent,
    BookViewerComponent,
    AddInfoComponent,
    MoreInfoComponent,
    SignUpSimpleComponent,
    MindsetshiftComponent,
    AboutComponent,
    ContactComponent,
    UserInfoComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    SummaryImageComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MaterialModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
    NgxUiLoaderHttpModule.forRoot({
      showForeground: true,
      exclude: [environment.baseUrl + 'ai/chat'],
    }),
    FormsModule,
    ReactiveFormsModule,
    CKEditorModule,
    QRCodeModule,
    MarkdownModule.forRoot(),
    SlickCarouselModule,
    PdfViewerModule,
    NgxDocViewerModule,
  ],
  providers: [
    AuthGuard,
    AuthService,
    LoginApiServiceService,
    AuthGuardAdmin,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useValue: '/',
    },
    {
      provide: ErrorHandler,
      useClass: UnhandledExceptionsHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    MarkdownService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
